<template>
  <FlotoAnchorLink :aria-label="$tc('update')" @click="triggerAction">
    <MIcon name="sync" class="action-menu-icon" />
    <span class="mx-1 action-menu-text">
      {{ $tc('update') }}
    </span>
    <FlotoDrawerForm
      :open="showForm"
      @submit="handleFormSubmit"
      @cancel="showForm = false"
    >
      <template v-slot:header>
        {{ $t('edit') }}
        <template v-if="selectedIds.length > 1">
          {{ $tc(moduleName, 2) }}
        </template>
        <template v-else>{{ $tc(moduleName) }}</template>
      </template>
      <MRow>
        <MCol
          v-if="
            moduleName !== $constants.CHANGE &&
            moduleName !== $constants.RELEASE &&
            hiddenFields.indexOf('statusId') === -1
          "
          :size="4"
        >
          <FlotoFormItem id="status-id-picker" :label="$tc('status')">
            <FlotoStatusPicker
              :value="formData.statusId"
              :module-name="moduleName || $constants.REQUEST"
              as-input
              @change="toggleField($event || undefined, 'statusId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('technicianId') === -1" :size="4">
          <FlotoFormItem id="technician-id-picker" :label="$t('assignee')">
            <FlotoTechnicianPicker
              show-load-indicator
              :value="formData.technicianId"
              :module-name="moduleName"
              change-technician-on-group-change
              :group-id="formData.groupId"
              as-input
              @change="toggleField($event, 'technicianId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('groupId') === -1" :size="4">
          <FlotoFormItem id="group-id-picker" :label="$t('technician_group')">
            <FlotoTechnicianGroupPicker
              :value="formData.groupId"
              as-input
              @change="toggleField($event, 'groupId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('urgencyId') === -1" :size="4">
          <FlotoFormItem id="urgency-id-picker" :label="$t('urgency')">
            <FlotoUrgencyPicker
              :value="formData.urgencyId"
              as-input
              @change="toggleField($event || undefined, 'urgencyId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('priorityId') === -1" :size="4">
          <FlotoFormItem id="priority-id-picker" :label="$t('priority')">
            <FlotoPriorityPicker
              :value="formData.priorityId"
              as-input
              @change="toggleField($event || undefined, 'priorityId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('impactId') === -1" :size="4">
          <FlotoFormItem id="impact-id-picker" :label="$t('impact')">
            <FlotoImpactPicker
              :value="formData.impactId"
              as-input
              @change="toggleField($event || undefined, 'impactId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('categoryId') === -1" :size="4">
          <FlotoFormItem id="category-id-picker" :label="$tc('category')">
            <FlotoCategoryPicker
              :value="formData.categoryId"
              :module-name="moduleName || $constants.REQUEST"
              @change="toggleField($event || undefined, 'categoryId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('locationId') === -1" :size="4">
          <FlotoFormItem id="location-id-picker" :label="$tc('location')">
            <FlotoLocationPicker
              :value="formData.locationId"
              :module-name="moduleName || $constants.REQUEST"
              @change="toggleField($event || undefined, 'locationId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol
          v-if="
            moduleName === $constants.REQUEST &&
            hiddenFields.indexOf('sourceId') === -1
          "
          :size="4"
        >
          <FlotoFormItem id="source-id-picker" :label="$tc('source')">
            <FlotoSourcePicker
              :value="formData.sourceId"
              as-input
              @change="toggleField($event || undefined, 'sourceId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('departmentId') === -1" :size="4">
          <FlotoFormItem id="department-id-picker" :label="$tc('department')">
            <FlotoDepartmentPicker
              :value="formData.departmentId"
              as-input
              :module-name="moduleName || $constants.REQUEST"
              @change="toggleField($event || undefined, 'departmentId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol
          v-if="hiddenFields.indexOf('companyId') === -1 && enableMsp"
          :size="4"
        >
          <FlotoFormItem id="company-id-picker" :label="$tc('company')">
            <CompanyPicker
              :value="formData.companyId"
              as-input
              @change="toggleField($event || undefined, 'companyId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol
          v-if="
            moduleName === $constants.PROBLEM &&
            hiddenFields.indexOf('knownError') === -1
          "
          :size="4"
        >
          <FlotoFormItem id="known-error-picker" :label="$tc('known_error')">
            <FlotoDropdownPicker
              :value="formData.knownError"
              as-input
              :options="[
                { text: $t('yes'), key: 'true' },
                { text: $t('no'), key: 'false' },
              ]"
              @change="toggleField($event, 'knownError')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol
          v-if="
            moduleName === $constants.PROBLEM &&
            hiddenFields.indexOf('natureOfProblem') === -1
          "
          :size="4"
        >
          <FlotoFormItem
            id="nature-of-problem-picker"
            :label="$tc('nature_of_problem')"
          >
            <FlotoDropdownPicker
              :value="formData.natureOfProblem"
              as-input
              :options="[
                { text: $t('proactive'), key: 'proactive' },
                { text: $t('reactive'), key: 'reactive' },
              ]"
              @change="toggleField($event || undefined, 'natureOfProblem')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol
          v-if="
            moduleName === $constants.PROBLEM &&
            hiddenFields.indexOf('businessServiceId') === -1
          "
          :size="4"
        >
          <FlotoFormItem
            id="business-service-id-picker"
            :label="$tc('business_service')"
          >
            <FlotoBusinessServicePicker
              :value="formData.businessServiceId"
              as-input
              @change="toggleField($event || undefined, 'businessServiceId')"
            />
          </FlotoFormItem>
        </MCol>
        <MCol v-if="hiddenFields.indexOf('fileAttachments') === -1" :size="12">
          <FlotoFormItem
            id="file-attachments-input"
            :label="$tc('attachment', 2)"
          >
            <FlotoAttachment
              :value="formData.fileAttachments"
              @change="
                toggleField(
                  $event.length ? $event : undefined,
                  'fileAttachments'
                )
              "
            />
          </FlotoFormItem>
        </MCol>

        <MCol v-if="hiddenFields.indexOf('tags') === -1" :size="12">
          <FlotoTagsPicker
            id="tags-picker"
            :value="formData.tags"
            @change="toggleField($event.length ? $event : undefined, 'tags')"
          />
        </MCol>
        <MCol class="mt-2" :size="12">
          <MCheckbox id="append-checkbox" v-model="formData.append">
            {{ $t('append_to_existing_tags') }}
          </MCheckbox>
        </MCol>
        <MDivider v-if="customFields.length" />
        <h5 v-if="customFields.length" class="px-6">
          {{ $t('custom_field') }}
        </h5>
        <MCol :size="12">
          <FormConsumer
            v-if="customFields.length"
            v-model="formData.fieldValueDetails"
            :form-fields="customFields"
            :with-submit="false"
            :focus-event-brodcast="false"
          />
        </MCol>
        <MCol v-if="shouldShowNoteForm" :size="12">
          <MDivider />
          <NoteRuleForm v-model="formData.note" />
        </MCol>
      </MRow>
      <template v-slot:actions="{ hide, submit }">
        <MButton
          id="update-btn"
          class="mr-2"
          :loading="processing"
          @click="submit"
        >
          {{ $t('update') }}
        </MButton>
        <MButton id="cancel-btn" variant="default" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawerForm>
  </FlotoAnchorLink>
</template>

<script>
import Omit from 'lodash/omit'
import Intersection from 'lodash/intersection'
import { StatusComputed } from '@state/modules/status'
import { CustomRulesComputed } from '@state/modules/custom-rules'
import NoteRuleForm from '@components/note-rules/note-rule-form'
import { bulkUpdateApi } from '../../ticket-api'
import { FormComputed } from '@state/modules/form'
import CompanyPicker from '@components/data-picker/company-picker'
import { MspConfigComputed } from '@state/modules/msp-config/helpers'
import { flattenFields, bulkActionsSupportedFields } from '@data/form'
import FormConsumer from '@components/form-consumer'
import { handleCheckRequiredNoteQualified } from '../../helpers/check-required-note-field'
import CloneDeep from 'lodash/cloneDeep'

export default {
  name: 'Update',
  components: { NoteRuleForm, CompanyPicker, FormConsumer },
  props: {
    selectedIds: { type: Array, required: true },
    moduleName: { type: String, required: true },
  },
  data() {
    return {
      showForm: false,
      processing: false,
      formData: {
        note: {},
        append: true,
      },
      customFieldUpdateAction: bulkActionsSupportedFields,
    }
  },
  computed: {
    ...StatusComputed,
    ...CustomRulesComputed,
    ...FormComputed,
    ...MspConfigComputed,
    shouldShowNoteForm() {
      const noteRequiredFields =
        this[`${this.moduleName}NoteRequiredFields`] || []

      const qualifiedRequiredNoteFields = CloneDeep(
        this[`${this.moduleName}QualifiedRequiredNoteFields`] || {}
      )
      return (
        Intersection(noteRequiredFields, Object.keys(this.formData)).length &&
        handleCheckRequiredNoteQualified(
          this.formData,
          noteRequiredFields,
          qualifiedRequiredNoteFields,
          this.moduleName
        )
      )
    },
    hiddenFields() {
      return this[`${this.moduleName}HiddenFields`] || []
    },
    customFields() {
      const allFields = flattenFields(
        this[`${this.moduleName}Form`].fields || []
      )
      const fields = allFields.filter(
        (f) => this.customFieldUpdateAction.indexOf(f.type) >= 0
      )
      const filteredFields = fields.filter((f) => {
        return (
          !f.attributes.systemManagedField ||
          f.attributes.systemManagedField === false
        )
      })
      let finalFields = [...filteredFields]
      finalFields.forEach((field) => {
        field.required = false
      })
      return finalFields
    },
  },
  methods: {
    triggerAction() {
      this.$emit('trigger')
      setTimeout(() => {
        this.showForm = true
      }, 400)
    },
    toggleField($event, fieldName) {
      let data
      if ($event !== 0 && typeof $event !== 'boolean' && !$event) {
        data = Omit(this.formData, [fieldName])
      } else {
        data = {
          ...this.formData,
          [fieldName]: $event,
        }
      }
      this.formData = data
    },
    handleFormSubmit() {
      this.processing = true
      const updatedFieldValueDetails = {}
      const fieldValueDetails = this.formData.fieldValueDetails || {}
      Object.keys(fieldValueDetails).forEach((key) => {
        if (fieldValueDetails[key]) {
          updatedFieldValueDetails[key] = fieldValueDetails[key]
        }
      })
      const data = {
        ...this.formData,
        ...this.formData.note,
        fieldValueDetails: updatedFieldValueDetails,
        note: undefined,
      }
      bulkUpdateApi(this.moduleName, this.selectedIds, data)
        .then((response) => {
          if (response.failed.length) {
            this.$emit('bulk-update-error', response.failed)
          }
          this.showForm = false
          this.$emit('done')
        })
        .catch(() => {
          this.processing = false
        })
    },
  },
}
</script>
